import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useAudio } from '../../../contexts/AudioContext';
import { FiPlus, FiTrash2, FiMic, FiFileText, FiUploadCloud } from 'react-icons/fi';
import { useData } from '../../../contexts/DataContext';
import { useToast } from '../../../contexts/ToastContext';
import { useSubscription, getUserTier } from '../../../contexts/SubscriptionContext';
import usePremiumAccess from '../../../hooks/usePremiumAccess';
import { AiAudioGenerationApi } from '../../../services/ai/AiAudioGenerationApi';
import { registerJob, completeJob, failJob } from '../../../hooks/useGenerationJobs';
import { AudioContentApi } from '../../../services/api/AudioContentApi';
import AudioUrlService from '../../../services/AudioUrlService';
import DocumentSelectionModal from '../../documents/DocumentSelectionModal';
import AudioLibraryPanel from '../../common/AudioLibraryPanel';
import AudioDetailView from '../../common/AudioDetailView';
import MiniGameLoadingOverlay from '../MiniGameLoadingOverlay';
import { UsageEventsTracker } from '../../../services/UsageEventsTracker';
import UpgradeModal from '../../common/UpgradeModal';
// AudioLibraryPanel handles library fetching/filtering internally
import './PodcastsTool.css';
import '../../primitives/Button.css';

const scoutPodcastImage = '/scout_images/scout_podcast.png';

// Default depth level for podcast generation
// Options: 'brief', 'moderate', 'comprehensive', 'exhaustive'
// Hardcoded to 'moderate' to provide balanced content without overwhelming users with choices
const DEFAULT_DEPTH_LEVEL = 'moderate';

/**
 * PodcastsTool - AI-powered podcast generation from selected documents
 * 
 * Features:
 * - Multi-document selection via DocumentSelectionModal
 * - Configurable podcast parameters (depth, title, description, voice config)
 * - Long-running synchronous generation (up to 10 minutes) with spinner messages
 * - Audio playback with native controls
 * - Podcast library with inline playback and delete functionality
 * - Responsive layout that adapts to panel width changes
 * - Theme token-based styling for automatic theme updates
 * - State persistence when switching tools
 * 
 * Note: Connection must stay open during generation to keep Cloud Run process alive
 */
export default function PodcastsTool({ 
  selectedCourse, 
  persistedState,
  onStateChange,
  isExpanded = false,
  isPanelExpanded = true,
  externalAudioSelection = null,
  externalShowGenerator = false,
  onClearAudioSelection
}) {
  const { playAudio: globalPlayAudio, startGeneration, updateGenerationStage, completeGeneration, failGeneration } = useAudio();
  const dataContext = useData();
  const getDocumentStatusIdByName = dataContext?.getDocumentStatusIdByName;
  const { addToast } = useToast();
  const { subscription, initialized } = useSubscription();
  const { canCreate, tier: currentTier, initialized: premiumInitialized } = usePremiumAccess();
  const containerRef = useRef(null);
  
  // Subscription gating state – blocks NEW creation, not library viewing
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const isCreationBlocked = (initialized || premiumInitialized) && !canCreate('podcasts');
  
  // Debug logging to verify subscription state
  useEffect(() => {
    console.log('[PodcastsTool] Subscription debug:', {
      initialized,
      subscription,
      currentTier,
      canCreate: canCreate('podcasts'),
      isCreationBlocked
    });
  }, [initialized, subscription, currentTier, isCreationBlocked, canCreate]);
  
  // Compute required status ID dynamically (with fallback to 7 for backward compatibility)
  const ragStatusId = typeof getDocumentStatusIdByName === 'function' 
    ? getDocumentStatusIdByName('RAG Chunks Processed') 
    : null;
  const requiredStatusIds = Array.isArray(ragStatusId) ? ragStatusId : (ragStatusId ? [ragStatusId] : [7]);
  const prevStateRef = useRef(null);
  const isMountedRef = useRef(true);
  const onStateChangeRef = useRef(onStateChange);
  const documentPickerRef = useRef(null);

  // ============ PERSISTED STATE ============
  // Note: selectedDocumentIds is NOT persisted - always starts fresh
  const [selectedDocumentIds, setSelectedDocumentIds] = useState([]);
  const [title, setTitle] = useState(
    persistedState?.title || ''
  );
  const [description, setDescription] = useState(
    persistedState?.description || ''
  );
  const [voiceConfig, setVoiceConfig] = useState(
    persistedState?.voiceConfig || ''
  );
  const [customSearchPrompt, setCustomSearchPrompt] = useState(
    persistedState?.customSearchPrompt || ''
  );
  const [generatedAudio, setGeneratedAudio] = useState(
    persistedState?.generatedAudio || null
  );

  // ============ UI STATE (not persisted) ============
  const [showDocModal, setShowDocModal] = useState(false);
  // const [containerWidth, setContainerWidth] = useState(0); // (not currently used)
  const [isGenerating, setIsGenerating] = useState(
    persistedState?.isGenerating || false
  );
  const [error, setError] = useState(null);
  const [lastPayload, setLastPayload] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  
  // Collapsed mode creation state
  const [isCreating, setIsCreating] = useState(false);
  const overlayRef = useRef(null);
  const [overlayHeight, setOverlayHeight] = useState(0);

  // Library state
  const [libraryRefreshKey, setLibraryRefreshKey] = useState(0);
  const [streamUrlById, setStreamUrlById] = useState({});

  // View mode state
  const [currentView, setCurrentView] = useState('idle'); // 'idle' | 'generating' | 'player'
  const [selectedLibraryItem, setSelectedLibraryItem] = useState(null);
  const [selectedAudioForDetail, setSelectedAudioForDetail] = useState(null);
  const [generationStage, setGenerationStage] = useState(null); // SSE progress stage text

  // Responsive layout measurements removed (no longer used)
  
  // ============ OVERLAY HEIGHT MEASUREMENT ============
  useEffect(() => {
    if (isCreating && overlayRef.current) {
      const height = overlayRef.current.offsetHeight;
      setOverlayHeight(height);
    } else {
      setOverlayHeight(0);
    }
  }, [isCreating]);

  // Track mounted state for generation survival across tab switches
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Keep onStateChangeRef in sync
  useEffect(() => {
    onStateChangeRef.current = onStateChange;
  }, [onStateChange]);



  // ============ EXTERNAL AUDIO SELECTION (from AIToolsPage sidebar) ============
  useEffect(() => {
    if (!externalAudioSelection) return;
    const audio = externalAudioSelection;
    console.log('[PodcastsTool] External audio selection received:', audio);

    // Clear player state so the detail view render condition (currentView !== 'player') is met
    if (currentView === 'player') {
      setCurrentView('idle');
      setSelectedLibraryItem(null);
    }
    // Show detail view
    setSelectedAudioForDetail(audio);
  }, [externalAudioSelection, currentView]);

  // ============ EXTERNAL SHOW GENERATOR (from AIToolsPage New button) ============
  useEffect(() => {
    if (externalShowGenerator) {
      console.log('[PodcastsTool] External show generator triggered');
      setCurrentView('idle');
      setSelectedLibraryItem(null);
      setSelectedAudioForDetail(null);
      // Clear the flag after handling
      if (onStateChange) {
        onStateChange({ ...persistedState, showGenerator: false });
      }
    }
  }, [externalShowGenerator, onStateChange, persistedState]);

  // ============ STATE PERSISTENCE ============
  useEffect(() => {
    if (!onStateChange) return;

    const currentState = {
      title,
      description,
      voiceConfig,
      customSearchPrompt,
      generatedAudio,
      isGenerating
    };

    const prevState = prevStateRef.current;
    if (!prevState || JSON.stringify(currentState) !== JSON.stringify(prevState)) {
      prevStateRef.current = currentState;
      onStateChange(currentState);
    }
  }, [title, description, voiceConfig, customSearchPrompt, generatedAudio, isGenerating, onStateChange]);

  // Library fetching / filtering is handled by <AudioLibraryPanel />

  // ============ DOCUMENT SELECTION ============
  const handleToggleDocument = useCallback((docId) => {
    setSelectedDocumentIds(prev => {
      const id = String(docId);
      if (prev.includes(id)) {
        return prev.filter(d => d !== id);
      } else {
        return [...prev, id];
      }
    });
  }, []);

  const handleConnectDocuments = useCallback((docIds) => {
    setSelectedDocumentIds(docIds.map(String));
    setShowDocModal(false);
  }, []);

  // ============ AUDIO SELECTION (from Library Panel) ============
  // Play audio via global AudioPlayer (persistent pop-out modal)
  const handlePlayAudio = useCallback(async (audio) => {
    if (!audio) return;
    
    console.log('[PodcastsTool] handlePlayAudio called with:', audio);
    
    // Update local tracking (library highlighting, etc.)
    setSelectedLibraryItem(audio);
    setSelectedAudioForDetail(null);
    
    // Resolve stream URL via documentId (audioUrl is raw GCS path, not playable)
    const docId = audio.documentId || audio.document_id;
    let resolvedUrl = streamUrlById[audio.id];
    if (!resolvedUrl && docId) {
      try {
        resolvedUrl = await AudioUrlService.getOrRefreshUrl(docId);
        setStreamUrlById(prev => ({ ...prev, [audio.id]: resolvedUrl }));
      } catch (err) {
        console.error('Failed to get stream URL:', err);
        return;
      }
    }
    
    if (!resolvedUrl) {
      console.error('[PodcastsTool] No stream URL resolved for audio:', audio.id);
      return;
    }

    // Delegate to global AudioPlayer context
    globalPlayAudio({
      id: audio.id,
      title: audio.title || 'Podcast',
      audioUrl: resolvedUrl,
      format: 'podcast',
      durationSeconds: audio.durationSeconds || audio.duration_seconds || 0,
      documentId: docId
    });
  }, [streamUrlById, globalPlayAudio]);

  // Play a freshly-generated result in the global player
  const playGeneratedInGlobalPlayer = useCallback(async (result) => {
    if (!result) return;
    const docId = result.document_id;
    let url = result.stream_url;
    // If stream_url is a document: placeholder, resolve a signed URL
    if (!url || url.startsWith('document:')) {
      try {
        url = await AudioUrlService.getOrRefreshUrl(docId);
      } catch (err) {
        console.error('[PodcastsTool] Failed to resolve URL for generated audio:', err);
        return;
      }
    }
    // Build a human-readable title from the result
    const titles = result.document_titles || [];
    const readableTitle = result.title
      || (titles.length ? `Podcast: ${titles.slice(0, 2).join(', ')}${titles.length > 2 ? ` (+${titles.length - 2} more)` : ''}` : null)
      || 'Generated Podcast';
    globalPlayAudio({
      id: result.audio_content_id || docId,
      title: readableTitle,
      audioUrl: url,
      format: 'podcast',
      durationSeconds: result.duration_seconds || 0,
      documentId: docId
    });
  }, [globalPlayAudio]);

  // In expanded mode, show detail view; in collapsed mode, play directly
  const handleSelectAudioFromPanel = useCallback(async (audio) => {
    if (!audio) return;
    // When in expanded/panel mode, show detail view instead of playing
    if (isPanelExpanded) {
      // Clear player state so detail view can show
      if (currentView === 'player') {
        setCurrentView('idle');
        setSelectedLibraryItem(null);
      }
      setSelectedAudioForDetail(audio);
      return;
    }
    // Collapsed mode: play directly
    handlePlayAudio(audio);
  }, [isPanelExpanded, handlePlayAudio, currentView]);

  // ============ PODCAST GENERATION ============
  const handleGeneratePodcast = useCallback(async () => {
    // Premium gate: block new creation for non-entitled users
    if (isCreationBlocked) {
      setShowUpgradeModal(true);
      return;
    }
    if (selectedDocumentIds.length === 0) return;

    setIsGenerating(true);
    setError(null);
    setGeneratedAudio(null); // Clear previous result

    let genJobId = null;

    const corrId = UsageEventsTracker.startCorrelation('podcast');

    UsageEventsTracker.recordUserAction('PODCAST_GENERATION_STARTED', {
      action: 'generate',
      category: 'ai_tool',
      label: 'podcast_start',
      value: selectedDocumentIds.length,
      metadata: {
        documentCount: selectedDocumentIds.length,
        courseId: selectedCourse?.id || selectedCourse?.endeavorId,
        source: 'podcasts_tool'
      }
    }, { correlationId: corrId });

    try {
      const endeavorId = selectedCourse?.id || selectedCourse?.endeavorId;
      
      // Parse voice config if provided
      let parsedVoiceConfig = undefined;
      if (voiceConfig.trim()) {
        try {
          parsedVoiceConfig = JSON.parse(voiceConfig);
        } catch (err) {
          setError('Invalid voice configuration JSON');
          setIsGenerating(false);
          return;
        }
      }

      const payload = {
        document_ids: selectedDocumentIds.map(Number),
        format: 'podcast',
        title: title?.trim() || undefined,
        description: description?.trim() || undefined,
        depth_level: DEFAULT_DEPTH_LEVEL,
        voice_config: parsedVoiceConfig,
        custom_search_prompt: customSearchPrompt?.trim() || undefined,
        ...(endeavorId ? { endeavor_id: endeavorId } : {})
      };

      setLastPayload(payload);

      // Register processing job for sidebar phantom card
      const genJob = registerJob({
        toolType: 'podcast',
        title: title?.trim() || 'Podcast',
        metadata: {
          documentCount: selectedDocumentIds.length,
        },
      });
      genJobId = genJob.id;

      // Track generation globally so the indicator persists across navigation
      startGeneration({ id: genJobId, toolType: 'podcast', title: title?.trim() || 'Podcast' });

      console.log('[PodcastsTool] Generating podcast with payload:', payload);
      
      setGenerationStage('Starting Generation');
      
      // Stream generation with SSE progress stages
      const result = await AiAudioGenerationApi.generateAudioStream(
        payload,
        (stage) => {
          console.log('[PodcastsTool] Generation stage:', stage);
          setGenerationStage(stage);
          updateGenerationStage(stage);
        }
      );
      console.log('[PodcastsTool] Generation result:', result);
      setGenerationStage(null);

      // Expect direct result with stream_url
      if (result && result.stream_url) {
        // Notify sidebar history panel to refresh
        window.dispatchEvent(new CustomEvent('audio:created', { detail: { format: 'podcast' } }));
        if (genJobId) completeJob(genJobId);
        UsageEventsTracker.recordUserAction('PODCAST_GENERATION_SUCCESS', {
          action: 'complete',
          category: 'ai_tool',
          entityType: 'audio',
          entityId: result.audio_id || result.id,
          label: 'podcast_generated',
          metadata: {
            audioId: result.audio_id || result.id,
            documentCount: selectedDocumentIds.length,
            source: 'podcasts_tool'
          }
        }, { correlationId: UsageEventsTracker.getCorrelation('podcast') });
        UsageEventsTracker.clearCorrelation('podcast');

        completeGeneration();

        if (isMountedRef.current) {
          setGeneratedAudio(result);
          // Don't call setIsGenerating(false) — let the overlay dismiss itself
          setLibraryRefreshKey(k => k + 1);
          playGeneratedInGlobalPlayer(result);
          addToast({ intent: 'success', message: '🎙️ Podcast generated successfully!' });
        } else {
          console.log('[PodcastsTool] Generation completed while unmounted, playing via global player');
          playGeneratedInGlobalPlayer(result);
          setIsGenerating(false);
          if (onStateChangeRef.current) {
            onStateChangeRef.current({
              title, description, voiceConfig, customSearchPrompt,
              generatedAudio: result,
              isGenerating: false,
            });
          }
        }
      } else {
        // Unexpected response format
        console.warn('[PodcastsTool] Unexpected response format:', result);
        setError('Unexpected response from server. The podcast may still be processing - check your library in a few minutes.');
        if (genJobId) failJob(genJobId, 'Unexpected response from server');
        setLibraryRefreshKey(k => k + 1); // Still refresh library in case it completed
      }
    } catch (err) {
      console.error('[PodcastsTool] Generation error:', err);
      setGenerationStage(null);
      
      UsageEventsTracker.recordUserAction('PODCAST_GENERATION_FAILED', {
        action: 'error',
        category: 'ai_tool',
        label: 'podcast_failed',
        metadata: {
          errorMessage: err.message,
          documentCount: selectedDocumentIds.length,
          source: 'podcasts_tool'
        }
      }, { correlationId: UsageEventsTracker.getCorrelation('podcast') });
      UsageEventsTracker.clearCorrelation('podcast');

      let errorMessage = 'Failed to generate podcast.';
      if (err.name === 'AbortError') {
        errorMessage = 'Request timed out after 10 minutes. The podcast may still complete in the background - check your Podcast Library in a few minutes.';
      } else if (err.message?.includes('404') || err.status === 404) {
        errorMessage = 'Podcast generation endpoint not available. The AI service may not have this feature deployed yet.';
      } else if (err.message?.includes('Network') || err.message?.includes('Failed to fetch')) {
        errorMessage = 'Network error. The podcast may still be generating - check your Podcast Library in a few minutes.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      failGeneration(errorMessage);
      if (genJobId) failJob(genJobId, errorMessage);
      // Don't call setIsGenerating(false) — let the overlay show the error state
      setLibraryRefreshKey(k => k + 1); // Refresh library in case it completed despite error
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDocumentIds, title, description, voiceConfig, customSearchPrompt, selectedCourse, addToast, isCreationBlocked]);



  const handleNewPodcast = useCallback(() => {
    if (isCreationBlocked) {
      setShowUpgradeModal(true);
      return;
    }
    setGeneratedAudio(null);
    setError(null);
    setIsGenerating(false);
    setTitle('');
    setDescription('');
    setVoiceConfig('');
    setCustomSearchPrompt('');
    setSelectedDocumentIds([]); // Clear document selection for fresh start
    // Switch back to CREATE mode
    setSelectedAudioForDetail(null);
    setSelectedLibraryItem(null);
    setCurrentView('idle');
  }, [isCreationBlocked]);

  // Delete selected podcast and return to CREATE mode
  const handleDeleteSelectedPodcast = useCallback(async () => {
    if (!selectedAudioForDetail) return;
    if (!window.confirm('Are you sure you want to delete this podcast?')) return;
    try {
      await AudioContentApi.deleteAudioContent(selectedAudioForDetail.id);
      addToast({ intent: 'success', message: 'Podcast deleted' });
      setSelectedAudioForDetail(null);
      setLibraryRefreshKey(k => k + 1);
    } catch (err) {
      console.error('Failed to delete podcast:', err);
      addToast({ intent: 'danger', message: 'Failed to delete podcast' });
    }
  }, [selectedAudioForDetail, addToast]);

  // ============ LIBRARY ACTIONS ============
  // Library actions are handled by <AudioLibraryPanel />

  const formatDuration = (seconds) => {
    const s = Math.max(0, Number(seconds) || 0);
    const m = Math.floor(s / 60);
    const r = Math.floor(s % 60);
    return `${m}:${String(r).padStart(2, '0')}`;
  };

  // ============ RENDER HELPERS ============

  const renderDocumentSelection = () => (
    <div className="control-group" ref={documentPickerRef}>
      <label className="control-label">Source Documents</label>
      <button 
        className="podcast-doc-selector"
        onClick={() => { if (isCreationBlocked) { setShowUpgradeModal(true); return; } setShowDocModal(true); }}
      >
        <div className="podcast-doc-selector__icon">
          {selectedDocumentIds.length === 0 ? <FiUploadCloud size={22} /> : <FiFileText size={22} />}
        </div>
        <div className="podcast-doc-selector__text">
          <span className="podcast-doc-selector__label">
            {selectedDocumentIds.length === 0 ? 'Select Documents' : `${selectedDocumentIds.length} Document${selectedDocumentIds.length > 1 ? 's' : ''} Selected`}
          </span>
          <span className="podcast-doc-selector__hint">
            {selectedDocumentIds.length === 0 ? 'Choose files to generate from' : 'Click to change selection'}
          </span>
        </div>
      </button>
    </div>
  );



  const renderBasicSettings = () => (
    <div className="podcast-settings">
      {renderDocumentSelection()}
      
      <div className="control-group">
        <label className="control-label" htmlFor="podcast-title">
          Title <span className="control-label__optional">(optional)</span>
        </label>
        <input
          id="podcast-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Chapter 5 Deep Dive"
          className="podcast-input"
        />
      </div>

      <div className="control-group">
        <label className="control-label" htmlFor="podcast-description">
          Focus Prompt <span className="control-label__optional">(optional)</span>
        </label>
        <textarea
          id="podcast-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={`e.g. "Focus on the key leadership theories and give real-world examples"`}
          className="podcast-textarea"
          rows={2}
        />
      </div>
    </div>
  );

  const renderGenerateButton = () => (
    <button
      className="ui-btn ui-btn--primary podcast-generate-btn"
      onClick={handleGeneratePodcast}
      disabled={selectedDocumentIds.length === 0 || isGenerating}
    >
      <FiMic size={16} />
      {isGenerating ? 'Generating...' : 'Generate Podcast'}
    </button>
  );

  // progress UI handled inline where needed

  const renderError = () => {
    if (!error) return null;

    return (
      <div className="podcast-error-card">
        <div className="error-icon">⚠️</div>
        <h3>Generation Issue</h3>
        <p>{error}</p>
        <div className="podcast-error-actions">
          <button 
            className="ui-btn ui-btn--neutral"
            onClick={() => {
              setError(null);
              setGeneratedAudio(null);
            }}
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  };

  const renderNowPlaying = () => {
    if (!generatedAudio) return null;

    return (
      <div className="podcast-now-playing">
        <h3>Podcast Ready</h3>
        <div className="podcast-player-card">
          <div className="podcast-player-info">
            <div className="podcast-player-title">
              {generatedAudio.title || generatedAudio.file_name || 'Generated Podcast'}
            </div>
            {generatedAudio.duration_seconds > 0 && (
              <div className="podcast-player-duration">
                {formatDuration(generatedAudio.duration_seconds)}
              </div>
            )}
          </div>

          <p style={{ color: '#aaa', fontSize: '0.9em', margin: '8px 0' }}>
            Playing in the global audio player.
          </p>

          <div className="podcast-player-actions">
            <button
              className="ui-btn ui-btn--sm ui-btn--primary"
              onClick={() => playGeneratedInGlobalPlayer(generatedAudio)}
            >
              Open in Player
            </button>
            <button
              className="ui-btn ui-btn--sm ui-btn--neutral"
              onClick={() => {
                setGeneratedAudio(null);
              }}
            >
              Dismiss
            </button>
          </div>

          {generatedAudio.cost_usd !== undefined && (
            <div className="podcast-player-details">
              <button 
                className="podcast-details-toggle"
                onClick={() => setShowDetails(!showDetails)}
              >
                Details {showDetails ? '▼' : '▶'}
              </button>
              {showDetails && (
                <div className="podcast-details-content">
                  <p>Cost: ${Number(generatedAudio.cost_usd).toFixed(4)}</p>
                  {generatedAudio.audio_content_id && (
                    <p className="podcast-detail-id">ID: {generatedAudio.audio_content_id}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Library rendering handled by <AudioLibraryPanel />

  // ============ MAIN RENDER ============
  // NOTE: We no longer block the entire tool for non-premium users.
  // Library/history viewing is always allowed; only new creation is gated via
  // handleGeneratePodcast → isCreationBlocked → showUpgradeModal.
  
  // Show AudioLibraryPanel when panel is not expanded
  if (!isPanelExpanded) {
    return (
      <div className="podcast-tool podcast-tool--collapsed" ref={containerRef}>
        {/* Header Row */}
        <div className="podcast-tool-header-row">
          <h2 className="podcast-tool-header-title">Podcasts</h2>
          
          <div className="podcast-tool-header-actions">
            <input
              type="text"
              placeholder="Search..."
              className="podcast-search-input"
              readOnly
            />
            
            <button
              className="ui-btn ui-btn--primary ui-btn--icon"
              onClick={() => {
                if (isCreationBlocked) { setShowUpgradeModal(true); return; }
                setIsCreating(true);
                setCurrentView('idle');
                setSelectedLibraryItem(null);
              }}
              title="Create New Podcast"
            >
              +
            </button>
          </div>
        </div>

        {/* Body */}
        <div 
          className="podcast-tool-body"
          style={{ '--create-overlay-offset': `${overlayHeight + 12}px` }}
        >
          {/* Loading State Overlay - fills entire panel */}
          {isGenerating && (
            <div className="podcast-loading-overlay podcast-loading-overlay--collapsed">
              <MiniGameLoadingOverlay
                loadStatus={error ? 'error' : (generatedAudio ? 'ready' : 'loading')}
                contentType="podcasts"
                onReady={() => {
                  setIsGenerating(false);
                }}
                onRetry={() => {
                  setError(null);
                  if (lastPayload) {
                    handleGeneratePodcast();
                  }
                }}
                onExit={() => {
                  setIsGenerating(false);
                  setError(null);
                }}
                canAbort={false}
                errorMessage={error || "Couldn't load podcast. Check connection."}
                isCompact={true}
                stageText={generationStage}
              />
            </div>
          )}

          {/* Create Podcast Overlay */}
          <div 
            ref={overlayRef}
            className={`create-overlay ${isCreating ? 'create-overlay--open' : ''}`}
          >
            {isCreating && (
              <div className="create-overlay-card">
                <div className="create-overlay-header">
                  <h3>Create New Podcast</h3>
                  <button 
                    className="ui-btn ui-btn--icon create-overlay-close"
                    onClick={() => {
                      setIsCreating(false);
                    }}
                    title="Close"
                  >
                    ✕
                  </button>
                </div>
                
                <div className="quiz-builder-panel quiz-builder-panel--compact">
                  {renderBasicSettings()}
                  {renderGenerateButton()}
                </div>
              </div>
            )}
          </div>

          {/* History Scroll */}
          <div className="podcast-history-scroll" style={{ paddingTop: 'var(--create-overlay-offset, 0px)' }}>
            {/* Hero Strip */}
            {!isCreating && (
              <div className="podcast-hero-strip">
                <img 
                  src="/scout_images/scout_podcast.png" 
                  alt="Scout Podcasts"
                  className="podcast-hero-strip__image"
                />
              </div>
            )}
            
            {/* Audio Player - shown when item is selected */}
            <AudioLibraryPanel
              key={libraryRefreshKey}
              format="podcast"
              onSelectItem={handleSelectAudioFromPanel}
              onNewItem={() => {
                if (isCreationBlocked) { setShowUpgradeModal(true); return; }
                setIsCreating(true);
                setCurrentView('idle');
                setSelectedLibraryItem(null);
              }}
              showNewButton={false}
              hideHeader={true}
              hideSearch={true}
              showSectionHeader={true}
            />
          </div>
        </div>

        {showDocModal && (
          <DocumentSelectionModal
            isOpen={showDocModal}
            selectedDocuments={selectedDocumentIds}
            onToggleDocument={handleToggleDocument}
            onConnectDocuments={handleConnectDocuments}
            onClose={() => setShowDocModal(false)}
            requiredStatusIds={requiredStatusIds}
            title="Select Documents for Podcast"
            description="Choose processed documents to generate podcast content from."
          />
        )}
      </div>
    );
  }

  return (
    <div className={`podcast-tool ${isExpanded ? 'podcast-tool--expanded' : ''}`} ref={containerRef}>
      <div className="podcast-tool-content">
        {/* Left Column - Audio Library Panel (only in expanded state) */}
        {isExpanded && (
          <div className="podcast-tool-sidebar">
            <AudioLibraryPanel
              key={libraryRefreshKey}
              format="podcast"
              activeItemId={selectedAudioForDetail?.id || selectedLibraryItem?.id}
              onSelectItem={handleSelectAudioFromPanel}
              onNewItem={() => {
                if (isCreationBlocked) { setShowUpgradeModal(true); return; }
                setIsCreating(false);
                setCurrentView('idle');
                setSelectedLibraryItem(null);
                setSelectedAudioForDetail(null);
              }}
              showNewButton={true}
            />
          </div>
        )}

        {/* Scout Image Column (only when NOT expanded) */}
        <div className="podcast-scout-column" style={{ display: isExpanded ? 'none' : 'flex' }}>
          <img 
            src={scoutPodcastImage} 
            alt="Scout Podcast Experience" 
            className="podcast-scout-image"
          />
        </div>

        {/* Tip Card - Listen on the go */}
        <div className="podcast-tipCard-container" style={{ display: isExpanded ? 'none' : 'flex' }}>
          <div className="podcast-tipCard">
            <div className="podcast-tipIcon">🎧</div>
            <div className="podcast-tipText">
              <h4>Listen anywhere</h4>
              <p>Download your podcast or audiobook and listen on the go. Perfect for walks, workouts, or commuting.</p>
            </div>
          </div>
        </div>

        {/* Right Column - Generator OR Player UI */}
        <div className="podcast-ui-column">
          {/* Workspace container (like quizzes/flashcards) */}
          <div className="podcast-tool-workspace">
            {/* Loading state - fills workspace */}
            {isExpanded && isGenerating && (
              <div className="podcast-loading-state podcast-loading-state--expanded">
                <MiniGameLoadingOverlay
                  loadStatus={error ? 'error' : (generatedAudio ? 'ready' : 'loading')}
                  contentType="podcasts"
                  onReady={() => {
                    setIsGenerating(false);
                  }}
                  onRetry={() => {
                    setError(null);
                    if (lastPayload) {
                      handleGeneratePodcast();
                    }
                  }}
                  onExit={() => {
                    setIsGenerating(false);
                    setError(null);
                  }}
                  canAbort={false}
                  errorMessage={error || "Couldn't load podcast. Check connection."}
                  isCompact={false}
                  stageText={generationStage}
                />
              </div>
            )}

            {/* Expanded Layout: CREATE mode or LISTEN mode */}
            {isExpanded && !isGenerating && (
            <div className="podcast-expanded-layout">
              {/* Left column: Scout studio panel */}
              <div className="podcast-studio-panel">
                <div className="podcast-studio-panel__frame">
                  <img 
                    src={scoutPodcastImage} 
                    alt="Scout Podcast Experience" 
                    className="podcast-scout-hero__img"
                  />
                </div>
                <div className="podcast-tipCard-container podcast-tipCard-container--expanded">
                  <div className="podcast-tipCard">
                    <div className="podcast-tipIcon">🎧</div>
                    <div className="podcast-tipText">
                      <h4>Listen anywhere</h4>
                      <p>Download your podcast or audiobook and listen on the go. Perfect for walks, workouts, or commuting.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right column: Generator (CREATE) or Player + actions (LISTEN) */}
              {selectedAudioForDetail ? (
                <div className="podcast-create-panel podcast-create-panel--listen">
                  <div className="podcast-listen-icon-actions">
                    <button
                      className="podcast-icon-btn"
                      onClick={handleNewPodcast}
                      title="Create New Podcast"
                    >
                      <FiPlus size={16} />
                    </button>
                    <button
                      className="podcast-icon-btn podcast-icon-btn--delete"
                      onClick={handleDeleteSelectedPodcast}
                      title="Delete Podcast"
                    >
                      <FiTrash2 size={15} />
                    </button>
                  </div>
                  <AudioDetailView
                    key={selectedAudioForDetail.id}
                    audioItem={selectedAudioForDetail}
                    format="podcast"
                    onBack={() => setSelectedAudioForDetail(null)}
                    hideActions
                  />
                </div>
              ) : (
                <div className="podcast-create-panel">
                  <div className="podcast-create-panel-header">
                    <div className="podcast-create-panel-header__icon">🎙️</div>
                    <h3 className="podcast-create-panel-header__title">Create a Podcast</h3>
                    <p className="podcast-create-panel-header__subtitle">
                      Transform your course materials into engaging audio episodes. Select documents, customize the style, and let Scout do the rest.
                    </p>
                  </div>
                  <div className="podcast-create-panel-controls">
                    {!generatedAudio && !error && (
                      <>
                        {renderBasicSettings()}
                        {renderGenerateButton()}
                      </>
                    )}
                    {error && renderError()}
                    {generatedAudio && renderNowPlaying()}
                  </div>
                </div>
              )}
            </div>
          )}
          </div>

          {/* Non-expanded generator */}
          {!isExpanded && (
            <div className="podcast-generator">
              {isGenerating ? (
                <div className="podcast-loading-state">
                  <MiniGameLoadingOverlay
                    loadStatus={error ? 'error' : (generatedAudio ? 'ready' : 'loading')}
                    contentType="podcasts"
                    onReady={() => {
                      setIsGenerating(false);
                    }}
                    onRetry={() => {
                      setError(null);
                      if (lastPayload) {
                        handleGeneratePodcast();
                      }
                    }}
                    onExit={() => {
                      setIsGenerating(false);
                      setError(null);
                    }}
                    canAbort={false}
                    errorMessage={error || "Couldn't load podcast. Check connection."}
                    stageText={generationStage}
                  />
                </div>
              ) : (
                <>
                  {!generatedAudio && !error && (
                    <>
                      {renderBasicSettings()}
                      {renderGenerateButton()}
                    </>
                  )}

                  {error && renderError()}
                  {generatedAudio && renderNowPlaying()}
                </>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Document Selection Modal */}
      {showDocModal && (
        <DocumentSelectionModal
          isOpen={showDocModal}
          selectedDocuments={selectedDocumentIds}
          onToggleDocument={handleToggleDocument}
          onConnectDocuments={handleConnectDocuments}
          onClose={() => setShowDocModal(false)}
          requiredStatusIds={requiredStatusIds}
          title="Select Documents for Podcasts"
          description="Choose processed documents to generate podcast content from."
        />
      )}

      {/* Paywall modal – shown when non-entitled user tries to generate */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        currentTier={currentTier}
        requiredTier="pro"
        featureName="Podcast Generator"
        featureDescription="Create AI-powered podcasts from your course documents and materials."
        triggerSource="audio"
      />
    </div>
  );
}
